import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateUser, type AdminUserUpdate } from "@/api/admin-api";
import type { UserPublic } from "@/api/auth-api";
import type { Role } from "@/store/auth-store";
import { useToast } from "@/hooks/use-toast";
import { isValidEmail } from "@/lib/email";
import { formatDateDMY } from "@/utils/dateFormat";

import { USERS_KEY } from "./UserTable";
import { ALL_ROLES, ROLE_META } from "./lookups";

export interface EditUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserPublic | null;
}

export function EditUserModal({ open, onOpenChange, user }: EditUserModalProps) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("TESTRAD");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [location, setLocation] = useState("");
  const [active, setActive] = useState(true);

  // Reset form whenever the dialog re-opens for a new user — otherwise
  // the previous edit's draft values bleed over.
  useEffect(() => {
    if (open && user) {
      setFullName(user.full_name ?? "");
      setEmail(user.email ?? "");
      setRole(user.role);
      setLicenseNumber(user.license_number ?? "");
      setLocation(user.location ?? "");
      setActive(user.active);
    }
  }, [open, user]);

  const update = useMutation({
    mutationFn: (body: AdminUserUpdate) => {
      if (!user) throw new Error("no user");
      return updateUser(user.id, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      toast({ title: "User updated", variant: "success" });
      onOpenChange(false);
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Update failed";
      toast({ title: "Update failed", description: detail, variant: "error" });
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    // Send only changed fields so the audit row's `changed` map stays accurate.
    const body: AdminUserUpdate = {};
    const trimmedName = fullName.trim();
    const trimmedLicense = licenseNumber.trim();
    if (trimmedName !== (user.full_name ?? "")) body.full_name = trimmedName || null;
    const trimmedEmail = email.trim();
    if (trimmedEmail !== (user.email ?? "")) body.email = trimmedEmail || null;
    if (role !== user.role) body.role = role;
    if (trimmedLicense !== (user.license_number ?? "")) {
      body.license_number = trimmedLicense || null;
    }
    const trimmedLocation = location.trim();
    if (trimmedLocation !== (user.location ?? "")) {
      body.location = trimmedLocation || null;
    }
    if (active !== user.active) body.active = active;
    // A malformed address is worse here than a missing one: it is what
    // the reset dialog pre-fills, so it would be carried forward to a
    // send that silently goes nowhere. Blank is allowed -- plenty of
    // staff accounts have none.
    if (trimmedEmail.length > 0 && !isValidEmail(trimmedEmail)) {
      toast({
        title: "That email address is not complete",
        description: "Correct it, or clear the field to leave the account without one.",
        variant: "error",
      });
      return;
    }
    if (Object.keys(body).length === 0) {
      toast({ title: "Nothing to save", variant: "default" });
      return;
    }
    update.mutate(body);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-border bg-background-card shadow-theme-lg"
          data-cy="edit-user-modal"
        >
          <div className="border-b border-border px-6 pt-6 pb-4">
            <Dialog.Title className="text-lg font-semibold text-foreground-heading">
              Edit user
            </Dialog.Title>
            {user ? (
              <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                Editing{" "}
                <span className="font-mono text-foreground">{user.username}</span> ·{" "}
                created {formatDateDMY(user.created_at)}
              </Dialog.Description>
            ) : null}
          </div>

          <form className="space-y-4 px-6 py-5" onSubmit={onSubmit}>
            <label className="block text-xs">
              <span className="sk-label">Full name</span>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                data-cy="edit-fullname"
                className="sk-input mt-1"
              />
            </label>

            {/* Added 2026-08-25. The backend has always accepted and
                audited `email` on this endpoint; nothing exposed it, so
                an address typed wrongly at creation could never be
                corrected -- and it is the value the reset dialog
                pre-fills its "Send to" box with. */}
            <label className="block text-xs">
              <span className="sk-label">Email address</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-cy="edit-email"
                placeholder="Blank = no address on file"
                className={[
                  "sk-input mt-1",
                  email.trim().length > 0 && !isValidEmail(email)
                    ? "sk-invalid"
                    : "",
                ].join(" ")}
                autoComplete="off"
                spellCheck={false}
              />
              <span
                className={[
                  "mt-1 block text-[11px]",
                  email.trim().length > 0 && !isValidEmail(email)
                    ? "text-status-urgent"
                    : "text-foreground-muted",
                ].join(" ")}
              >
                {email.trim().length > 0 && !isValidEmail(email)
                  ? "That is not a complete email address."
                  : "Where a password reset can be sent. Optional for staff."}
              </span>
            </label>

            <label className="block text-xs">
              <span className="sk-label">Role</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                data-cy="edit-role"
                className="sk-input mt-1"
              >
                {ALL_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_META[r].label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-xs">
              <span className="sk-label">Licence number</span>
              <input
                type="text"
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
                data-cy="edit-license"
                className="sk-input mt-1 font-mono"
              />
            </label>

            <label className="block text-xs">
              <span className="sk-label">Location</span>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                data-cy="edit-location"
                placeholder="Blank = sees all studies"
                className="sk-input mt-1 font-mono"
              />
              <span className="mt-1 block text-[11px] text-foreground-muted">
                Sonographers and reception only see studies matching this
                location. Blank means no restriction.
              </span>
            </label>

            <div className="flex items-center justify-between rounded-lg border border-border bg-table-row-alt px-3 py-2">
              <div>
                <div className="text-sm text-foreground">Active</div>
                <div className="text-xs text-foreground-muted">
                  Inactive users cannot log in. Audit history is preserved either way.
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={active}
                data-cy="edit-active"
                onClick={() => setActive((v) => !v)}
                className={[
                  "relative h-6 w-11 rounded-full transition-colors",
                  active ? "bg-status-done" : "bg-border-strong",
                ].join(" ")}
              >
                <span
                  className={[
                    "absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform shadow-theme-sm",
                    active ? "translate-x-5" : "translate-x-0.5",
                  ].join(" ")}
                />
              </button>
            </div>

            <div className="-mx-6 mt-2 flex items-center justify-end gap-2 border-t border-border bg-table-row-alt px-6 py-4">
              <Dialog.Close
                data-cy="btn-cancel-edit"
                className="rounded-lg border border-border bg-transparent px-3 py-1.5 text-sm text-foreground hover:bg-table-row-hover"
              >
                Cancel
              </Dialog.Close>
              <button
                type="submit"
                data-cy="btn-save-user"
                disabled={update.isPending}
                className="sk-btn sk-btn-primary sk-sm"
              >
                {update.isPending ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
